import { ExtractedRecord, ExtractedRecordRule, TargetSchema } from "@/types";

import { getRecordSchemas, getRecordValidationErrors } from "./recordUtils";

/**
 * Grouping errored records by REASON, for the entry point above the schema tabs
 * (#6853).
 *
 * WHY REASON AND NOT SCHEMA. The story originally specified a per-schema split
 * — "2 in Claim Items, 1 in Claimants". Looking at a 3-error and a 296-error
 * fixture side by side (2026-09-09) settled it the other way, for a structural
 * reason:
 *
 *   Error count scales with rows. Reason count does NOT.
 *
 * The validation rules are a closed set of six, and a real document trips one
 * or two. So 3 errors and 300 produce the same size surface, which is why this
 * design needs no different answer at different densities — it dissolves the
 * question rather than answering it.
 *
 * Reason also implies the action, and schema does not. "296 rows have no
 * claimant identity" says *check the Member ID mapping*; "197 in Claim Items"
 * says nothing about what to do. The reviewer's two resolutions are both SET
 * operations — one mis-mapped column causing 300 errors is one problem fixed
 * once, and 300 junk rows want one select-and-exclude — so what they need is to
 * characterise the set, then act on it whole.
 *
 * Schema counts survive as SECONDARY navigation: they are still how you get to
 * the right tab, which is the blind spot that killed the stepper (an errors
 * affordance scoped to one tab shows none while three sit in another).
 */

/** Which resolutions a reason admits. Drives the copy, not just the grouping. */
export type ErrorResolution = "fix-mapping" | "exclude-rows";

export interface ErroredRecordGroup {
  /** The rule, or "unknown" for errors carrying no rule (older records). */
  rule: ExtractedRecordRule | "unknown";
  /** Reviewer-facing summary of the reason, without a count. */
  label: string;
  /** Resolutions this reason admits, most likely first. */
  resolutions: ErrorResolution[];
  /** DISTINCT errored records in this group, not error instances. */
  recordCount: number;
  /** Distinct records per schema, for the secondary tab navigation. */
  recordCountBySchema: Partial<Record<TargetSchema, number>>;
  /**
   * The same breakdown as INDICES rather than counts.
   *
   * Exposed so a consumer can net out excluded records exactly (!4682 review).
   * The banner needs per-schema counts AFTER exclusions, and a count cannot be
   * decremented correctly: a record carries its own set of schemas, so
   * excluding two claim-item rows must not reduce the claimant number.
   * Scaling by the group's remaining fraction would be a guess wearing the
   * costume of a count. The grouping already holds these sets internally —
   * this only stops collapsing them.
   */
  recordIndicesBySchema: Partial<Record<TargetSchema, number[]>>;
  /** Absolute record_index values, ascending — for select-all + exclude. */
  recordIndices: number[];
}

/**
 * Reason metadata. Deliberately a total map over the rule union rather than a
 * lookup with a default, so adding a rule to `ExtractedRecordRule` is a
 * compile error here — a new rule silently landing in an "Other" bucket is how
 * a reviewer stops being told what is wrong with their document.
 */
const RULE_META: Record<ExtractedRecordRule, { label: string; resolutions: ErrorResolution[] }> = {
  claimant_identification: {
    label: "No claimant identity — neither a member ID nor a name",
    // Mapping first: a whole column of blank member IDs is almost always a
    // mis-mapped column rather than hundreds of genuinely anonymous rows.
    resolutions: ["fix-mapping", "exclude-rows"],
  },
  insufficient_data: {
    label: "Too few fields to be a usable record",
    // Exclude first: this fires on junk rows produced from misread metadata,
    // which no mapping change rescues.
    resolutions: ["exclude-rows", "fix-mapping"],
  },
  policy_attribution: {
    label: "Could not be attributed to a policy",
    resolutions: ["fix-mapping", "exclude-rows"],
  },
  transformation_failed: {
    label: "A mapped value could not be transformed",
    resolutions: ["fix-mapping", "exclude-rows"],
  },
  unsupported_mapping_source: {
    label: "Mapped from a source this document does not support",
    resolutions: ["fix-mapping"],
  },
  state_abbrev_invalid: {
    label: "Invalid state abbreviation",
    resolutions: ["fix-mapping", "exclude-rows"],
  },
};

const UNKNOWN_META = {
  label: "Validation failed for an unrecorded reason",
  // Both offered: with no rule we cannot say which applies, and guessing
  // would send the reviewer down the wrong one.
  resolutions: ["fix-mapping", "exclude-rows"] as ErrorResolution[],
};

/**
 * An errored record paired with its absolute position.
 *
 * The index is REQUIRED and absolute. A group whose whole purpose is
 * select-all-and-exclude must carry the indices the exclusion is expressed in
 * (#6755), and deriving them from array position is what #6749/#6755 spent two
 * rounds removing.
 */
export interface IndexedErroredRecord {
  recordIndex: number;
  record: ExtractedRecord;
}

/**
 * Reason metadata for a rule, falling back for a rule this build does not know.
 *
 * `RULE_META` is a TOTAL map over the TS union, which gives a compile error
 * when a rule is added to the union — valuable, and kept. But `rule` arrives
 * from the SERVER, so the compile-time guarantee only covers the direction
 * where someone edits the TypeScript first. A backend rule this build has
 * never heard of yields `undefined`, and reading `.label` off it throws inside
 * `useErroredRecords`' try block — degrading the whole surface to "Could not
 * check this document for errored records", which reads as transient and is
 * not (!4682 review).
 *
 * The `unknown` bucket already exists with both resolutions, so an
 * unrecognised rule lands somewhere honest instead of taking the banner down.
 */
export const ruleMeta = (rule: ExtractedRecordRule | "unknown"): { label: string; resolutions: ErrorResolution[] } =>
  rule === "unknown" ? UNKNOWN_META : (RULE_META[rule] ?? UNKNOWN_META);

/**
 * Group errored records by reason.
 *
 * Counts DISTINCT RECORDS, not error instances. A record with two errors under
 * one rule counts once in that group; a record with errors under two rules
 * counts once in each, so group counts can sum to more than the record total.
 * That is the same deliberate arithmetic as per-schema counts, and it is right
 * for this surface: the reviewer acts on records, and a record with two
 * problems appears under both problems.
 */
export const groupErroredRecords = (records: IndexedErroredRecord[]): ErroredRecordGroup[] => {
  const byRule = new Map<
    ExtractedRecordRule | "unknown",
    { indices: Set<number>; bySchema: Map<TargetSchema, Set<number>> }
  >();

  for (const { recordIndex, record } of records) {
    const errors = getRecordValidationErrors(record);
    if (errors.length === 0) continue;

    // The record's schemas, not the error's. The error's `schema` says which
    // schema the RULE is about (the claimant handler labels everything
    // "claimant" even when it routed a claim_item record), while the tabs are
    // keyed on which schemas the RECORD carries. Using the error's schema here
    // would navigate the reviewer to a tab the record does not appear in —
    // which is precisely how the stepper failed.
    const recordSchemas = getRecordSchemas(record);

    // Iterated directly, NOT through a Set of distinct rules. An earlier
    // version deduped the rules first, which looked like it was what made
    // "two errors under one rule count once" true. It was not: `entry.indices`
    // is a Set of record indices, so adding the same index twice is already a
    // no-op, and removing the rule dedup changed no test. A guard that cannot
    // fail is worse than no guard — it reads as the reason the invariant holds
    // and stops anyone looking for the real one.
    //
    // The real reason is the index Set below. That is what the
    // "counts DISTINCT RECORDS, not error instances" test pins.
    for (const error of errors) {
      const rule: ExtractedRecordRule | "unknown" = error.rule ?? "unknown";
      let entry = byRule.get(rule);
      if (!entry) {
        entry = { indices: new Set(), bySchema: new Map() };
        byRule.set(rule, entry);
      }
      entry.indices.add(recordIndex);
      for (const schema of recordSchemas) {
        let set = entry.bySchema.get(schema);
        if (!set) {
          set = new Set();
          entry.bySchema.set(schema, set);
        }
        set.add(recordIndex);
      }
    }
  }

  const groups: ErroredRecordGroup[] = [];
  for (const [rule, entry] of byRule) {
    const meta = ruleMeta(rule);
    const bySchema: Partial<Record<TargetSchema, number>> = {};
    const indicesBySchema: Partial<Record<TargetSchema, number[]>> = {};
    for (const [schema, set] of entry.bySchema) {
      bySchema[schema] = set.size;
      indicesBySchema[schema] = [...set].sort((a, b) => a - b);
    }

    groups.push({
      rule,
      label: meta.label,
      resolutions: meta.resolutions,
      recordCount: entry.indices.size,
      recordCountBySchema: bySchema,
      recordIndicesBySchema: indicesBySchema,
      recordIndices: [...entry.indices].sort((a, b) => a - b),
    });
  }

  // Largest group first: the reviewer's leverage is highest on the reason
  // affecting the most rows, and that is usually the one mis-mapped column.
  // Tie-broken on rule so the order is stable across renders rather than
  // depending on Map insertion, which depends on which page arrived first.
  return groups.sort((a, b) => b.recordCount - a.recordCount || a.rule.localeCompare(b.rule));
};

/**
 * Copy for a group's resolutions. Named here rather than in the component so
 * #6853's AC5 — both paths stated, not inferred — has one source, and the
 * approve tooltip and the banner cannot drift.
 */
export const resolutionLabel = (r: ErrorResolution): string =>
  r === "fix-mapping" ? "Adjust the field mapping" : "Exclude these rows";

/**
 * The sentence for a group. Kept out of JSX so it is testable without
 * rendering, and so the ordering guarantee above is visible next to it.
 */
export const groupSentence = (group: ErroredRecordGroup, recordCount = group.recordCount): string => {
  const rows = recordCount === 1 ? "1 row" : `${recordCount.toLocaleString()} rows`;
  return `${rows}: ${group.label}`;
};
